import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';

const CreateCoupon = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    bonusType: '',
    bonusValue: '',
    maxBonusAmount: '',
    minOrderAmount: '0',
    maxUsage: '',
    validFrom: '',
    validUntil: '',
    applicableFor: 'all',
    isActive: true,
    description: '',
    image: null
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error('File size must be 2MB or less.');
        e.target.value = '';
        return;
      }
      setFormData(prev => ({ ...prev, image: file }));
      setImagePreview(URL.createObjectURL(file));
    } else {
      setFormData(prev => ({ ...prev, image: null }));
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();
      
      // Add all non-image fields first
      Object.keys(formData).forEach(key => {
        if (key !== 'image') {
          submitData.append(key, formData[key] !== null && formData[key] !== undefined ? formData[key] : '');
        }
      });
      
      // Add image file if selected
      if (formData.image && formData.image instanceof File) {
        submitData.append('image', formData.image);
      }

      const response = await apiService.postFormData('updateCoupon', submitData);

      toast.success('Coupon created successfully');
      navigate('/view-coupons');
    } catch (error) {
      toast.error('Failed to create coupon');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        {/* Breadcrumb */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 text-xs">
            <li className="inline-flex items-center">
              <a href="/dashboard" className="text-gray-600 hover:text-[#009ef7]">Home</a>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <a href="/view-coupons" className="text-gray-600 hover:text-[#009ef7]">Coupons</a>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <span className="text-gray-500">Add Coupon</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Add New Coupon</h3>
          </div>
          
          <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coupon Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bonus Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="bonusType"
                    value={formData.bonusType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  >
                    <option value="">Select Type</option>
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bonus Value <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="bonusValue"
                    value={formData.bonusValue}
                    onChange={handleInputChange}
                    min="0"
                    max="50"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Bonus Amount
                  </label>
                  <input
                    type="number"
                    name="maxBonusAmount"
                    value={formData.maxBonusAmount}
                    onChange={handleInputChange}
                    placeholder="For percentage coupons"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Order Amount
                  </label>
                  <input
                    type="number"
                    name="minOrderAmount"
                    value={formData.minOrderAmount}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Usage
                  </label>
                  <input
                    type="number"
                    name="maxUsage"
                    value={formData.maxUsage}
                    onChange={handleInputChange}
                    placeholder="Leave empty for unlimited"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valid From <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valid Until <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Applicable For <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="applicableFor"
                    value={formData.applicableFor}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  >
                    <option value="all">All Users</option>
                    <option value="first">First Order Only</option>
                    <option value="signup">New Signup Only</option>
                  </select>
                </div>

                <div className="flex items-center mt-8">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-[#009ef7] focus:ring-[#009ef7] border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Active</label>
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Coupon Banner
                </label>
                <input
                  type="file"
                  name="image"
                  onChange={handleFileChange}
                  accept=".png,.jpg,.jpeg,.webp"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Allowed file types: png, jpg, jpeg, webp. Max size: 2MB
                </p>
                {imagePreview && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                    <img
                      src={imagePreview}
                      alt="Coupon banner preview"
                      className="w-48 h-32 object-cover border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-center space-x-4">
                <button
                  type="button"
                  onClick={() => navigate('/view-coupons')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCoupon;